import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Tooltip, Typography } from 'antd';
import React, { useRef, useState, useEffect } from 'react';
import Icon1 from '@/assets/images/logo-selector/icons/icon1.svg?react';
import Icon2 from '@/assets/images/logo-selector/icons/icon2.svg?react';
import Icon3 from '@/assets/images/logo-selector/icons/icon3.svg?react';
import Icon4 from '@/assets/images/logo-selector/icons/icon4.svg?react';
import Icon5 from '@/assets/images/logo-selector/icons/icon5.svg?react';
import Icon6 from '@/assets/images/logo-selector/icons/icon6.svg?react';
import Icon7 from '@/assets/images/logo-selector/icons/icon7.svg?react';
import Icon8 from '@/assets/images/logo-selector/icons/icon8.svg?react';
import AddAgentIcon from './icons/add-agent.react.svg?react';
import AgentDelete from './icons/agent-delete.react.svg?react';
import AgentIcon from './icons/agent.react.svg?react';
import styles from './index.module.scss';

// Logo 配置映射
const LOGO_CONFIG: Record<string, React.ComponentType> = {
  icon1: Icon1,
  icon2: Icon2,
  icon3: Icon3,
  icon4: Icon4,
  icon5: Icon5,
  icon6: Icon6,
  icon7: Icon7,
  icon8: Icon8,
};

// 获取 agent logo 图标
const getAgentLogo = (logo?: string): React.ReactNode => {
  const logoKey = logo || 'icon1';
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const LogoComponent = LOGO_CONFIG[logoKey] || LOGO_CONFIG.icon1;
  return <LogoComponent />;
};

export interface AgentOption {
  value: string;
  label: string;
  description?: string;
  logo?: string;
}

interface AgentToolsProps {
  agents: string[];
  agentOptions: AgentOption[];
  onRemoveAgent: (agentValue: string) => void;
  onOpenAgentDrawer: () => void;
}

export const AgentTools: React.FC<AgentToolsProps> = ({
  agents,
  agentOptions,
  onRemoveAgent,
  onOpenAgentDrawer,
}) => {
  const agentListRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [needScroll, setNeedScroll] = useState(false);

  // 检查滚动状态
  const checkScrollStatus = () => {
    if (agentListRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = agentListRef.current;
      const hasOverflow = scrollWidth > clientWidth;
      setNeedScroll(hasOverflow);
      setCanScrollLeft(hasOverflow && scrollLeft > 0);
      setCanScrollRight(hasOverflow && scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  // 左滚动
  const scrollLeft = () => {
    if (agentListRef.current) {
      agentListRef.current.scrollBy({ left: -100, behavior: 'smooth' });
    }
  };

  // 右滚动
  const scrollRight = () => {
    if (agentListRef.current) {
      agentListRef.current.scrollBy({ left: 100, behavior: 'smooth' });
    }
  };

  // 监听滚动和窗口大小变化
  useEffect(() => {
    const agentList = agentListRef.current;
    if (agentList) {
      agentList.addEventListener('scroll', checkScrollStatus);
      window.addEventListener('resize', checkScrollStatus);

      // 初始检查
      checkScrollStatus();

      return () => {
        agentList.removeEventListener('scroll', checkScrollStatus);
        window.removeEventListener('resize', checkScrollStatus);
      };
    }
    return undefined;
  }, [agents]);

  // 当 agents 变化时重新检查滚动状态
  useEffect(() => {
    setTimeout(checkScrollStatus, 100);
  }, [agents]);

  return (
    <div className={styles.agentTools}>
      {needScroll && (
        <div
          className={`${styles.scrollArrow} ${!canScrollLeft ? styles.disabled : ''}`}
          onClick={scrollLeft}
        >
          <LeftOutlined />
        </div>
      )}
      <div className={styles.agentList} ref={agentListRef}>
        {agents.map((agentValue: string) => {
          const agent = agentOptions.find((opt: AgentOption) => opt.value === agentValue);
          if (!agent) return null;

          const tooltipContent = (
            <div className={styles.agentTooltipContent}>
              <div className={styles.agentTooltipTitle}>
                <AgentIcon />
                <span>{agent.label}</span>
              </div>
              <div className={styles.agentTooltipDescription}>
                <Typography.Paragraph ellipsis={{
                  rows: 5,
                }}>
                  {agent.description}
                </Typography.Paragraph>
              </div>
            </div>
          );

          return (
            <Tooltip
              key={agentValue}
              title={tooltipContent}
              placement="top"
              overlayClassName={styles.agentTooltip}
            >
              <div className={styles.agentItem} onClick={onOpenAgentDrawer}>
                <span className={styles.agentItemIcon}>{getAgentLogo(agent.logo)}</span>
                <span className={styles.agentItemLabel}>{agent.label}</span>
                {agents.length > 1 && (
                  <span
                    className={styles.agentItemClose}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveAgent(agentValue);
                    }}
                  >
                    <AgentDelete />
                  </span>
                )}
              </div>
            </Tooltip>
          );
        })}
        <span
          className={styles.addAgentBtn}
          onClick={onOpenAgentDrawer}
        >
          <AddAgentIcon />
        </span>
      </div>
      {needScroll && (
        <div
          className={`${styles.scrollArrow} ${!canScrollRight ? styles.disabled : ''}`}
          onClick={scrollRight}
        >
          <RightOutlined />
        </div>
      )}
    </div>
  );
};
